import { ChangeDetectorRef, Component, DestroyRef, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { combineLatest } from 'rxjs';
import { MessageService } from 'primeng/api';
import { PhysicianAccountPgpDTO } from '@mynexus/mynexus-ui-lib';
import { AuthDetailsPac, AuthRequestPacCommercialState, AuthRequestPACIMState, DocumentsPAC, OrderingProviderExistingData, ServicingFacilityExistingData } from '@shared/model';
import {
  AuthorizationDetailsPacimService,
  DiagnosisPacimService,
  DocumentPacimService,
  OrderingProviderPacimService,
  ServicingFacilitiesPacimService
} from '@core/service';
import { PACIMPgpWithProvider, PACIMProviderAccountPgpsDTO, PACIMProviderPgpDTO } from '@core/model';
import { GeneralErrorMessage } from '@core/constants/messages';
import {
  AuthRequestPacimStateManagerService
} from '@feature/auth-request-pacim/auth-request-pacim-state-manager.service';
import { orderDiagnosesByType } from '@shared/utility';
import { AuthRequestPacCommercialStateManagerService } from '@pages/auth-request-pac-commercial/services/auth-request-pac-commercial-state-manager.service';

@Component({
    // eslint-disable-next-line @angular-eslint/component-selector
    selector: 'preview-tab-pac',
    templateUrl: './preview-tab-pac.component.html',
    styleUrls: ['./preview-tab-pac.component.scss'],
    standalone: false
})
export class PreviewTabPacComponent implements OnChanges {
  @Input() set authRequestState(value: AuthRequestPACIMState | AuthRequestPacCommercialState) {
    if (!(value as AuthRequestPACIMState).authorization && !(value as AuthRequestPacCommercialState).authDetails) {
      return;
    }

    this.authRequestStateData = value;
    this.authData = (value as AuthRequestPACIMState).authorization || (value as AuthRequestPacCommercialState).authDetails;
    this.documentsData = (value as AuthRequestPacCommercialState).documents.documentsList ||  (value as AuthRequestPACIMState).documents;
  };

  get authRequestState(): AuthRequestPACIMState | AuthRequestPacCommercialState {
    return this.authRequestStateData;
  }
  @Input() tenantId!: string;
  @Input() tenantName!: string;
  @Input() isInternal: boolean;
  @Input() isPacCommercial: boolean;
  // Setting as optional since we are not using this check in PAC Commercial but
  // the preview tab component is shared between PAC IM and PAC Commercial
  @Input() isAnalyticsEligible?: boolean;
  @Input() isGoldCardVisible = false;

  @Input() authorizationPACToPACId = '';
  @Input() isPASComplete = false;

  @Output() cancelProcess = new EventEmitter<void>();
  @Output() backStep = new EventEmitter<void>();
  @Output() submitStep = new EventEmitter<void>();

  informationConfirmed: boolean;

  isSubmitting = false;
  authData: AuthDetailsPac;
  documentsData: DocumentsPAC[];

  private authRequestStateData: AuthRequestPACIMState | AuthRequestPacCommercialState;

  constructor(
    private authorizationDetailsService: AuthorizationDetailsPacimService,
    private diagnosisService: DiagnosisPacimService,
    private servicingFacilityService: ServicingFacilitiesPacimService,
    private orderingProviderService: OrderingProviderPacimService,
    private documentPacimService: DocumentPacimService,
    private cdr: ChangeDetectorRef,
    private messageService: MessageService,
    private stateManagerPacimService: AuthRequestPacimStateManagerService,
    private stateManagerPacCommercialService: AuthRequestPacCommercialStateManagerService,
    private destroyRef: DestroyRef,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.isPASComplete?.currentValue) {

      combineLatest([
        this.authorizationDetailsService.getAuthorizationDetails(this.tenantId, this.authorizationPACToPACId),
        this.diagnosisService.getDiagnosesByProductId(this.tenantId, this.authorizationPACToPACId),
        this.servicingFacilityService.getFacilityAccountsByProductId(this.tenantId, this.authorizationPACToPACId),
        this.orderingProviderService.getAuthOrderingProvider(this.tenantId, this.authorizationPACToPACId),
        this.documentPacimService.getFilesMetaDataByProductId(this.tenantId, this.authorizationPACToPACId),
      ])
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: ([authorization, diagnoses, facility, ordProvider, documents]) => {
            const orderDiagnoses = orderDiagnosesByType(diagnoses?.current);
            const servicingFacility = this.serializeFacility(facility);
            const orderingProvider = this.serializeOrderingProvider(ordProvider);
            const docs = documents.map(doc => ({
              documentFileName: doc.documentFileName,
              fileContent: doc.fileContent,
              documentTypes: [doc.documentType],
              documentSource: doc.documentSource,
              dateTimeFaxReceived: doc.dateTimeFaxReceived.toString(),
              description: doc.documentDescription,
              fileMetaData: { size: 0, type: ''},
              tenantId: doc.tenantId,
            }));

            if (this.isPacCommercial) {
              this.stateManagerPacCommercialService.setTabState('authDetails', authorization);
              this.stateManagerPacCommercialService.setTabState('diagnoses', orderDiagnoses);
              this.stateManagerPacCommercialService.setTabState('servicingFacility', servicingFacility);
              this.stateManagerPacCommercialService.setTabState('orderingProvider', orderingProvider);
              this.stateManagerPacCommercialService.setTabState('documents', {
                documentsList: docs,
                savedDocumentsIds: [],
              });
            } else {
              this.stateManagerPacimService.setTabState('authorization', authorization);
              this.stateManagerPacimService.setTabState('diagnoses', orderDiagnoses);
              this.stateManagerPacimService.setTabState('servicingFacility', servicingFacility);
              this.stateManagerPacimService.setTabState('orderingProvider', orderingProvider);
              this.stateManagerPacimService.setTabState('documents', docs);
            }

            this.cdr.markForCheck();
          },
          error: () => {
            this.messageService.add({
              key: 'httpActionKey',
              severity: 'error',
              summary: 'Error',
              detail: GeneralErrorMessage,
              life: 5000
            });
          }
        })
    }
  }

  onClickBack(): void {
    this.backStep.emit();
  }

  onCancel(): void {
    this.cancelProcess.emit();
  }

  onSubmit(): void {
    this.isSubmitting = true;

    this.submitStep.emit();
  }

  private serializeFacility(
    facility: PACIMProviderAccountPgpsDTO
  ): ServicingFacilityExistingData {
    const facilityContact = {
      contactFax: null,
      contactName: null,
      contactPhone: null,
      contactPhoneExtension: null,
    };

    if (facility) {
      const pgp: PACIMProviderPgpDTO = facility.providerPgpDTOList
        .find(item =>
          item.providerAccountId === facility.providerAccountDTO.providerAccountId);

      return {
        provider: {
          servicingProviderAccountId: facility.providerAccountDTO.providerAccountId,
          servicingProviderPgpId: pgp.providerPgpId,
          servicingProviderPgpsDTO: {
            ...facility.providerAccountDTO,
            authorizationPhone: pgp.authorizationPhone,
            authorizationFax: pgp.authorizationFax,
            authorizationFaxConfirmed: pgp.authorizationFaxConfirmed,
            authorizationFaxConfirmedDate: pgp.authorizationFaxConfirmedDate,
            facilityServiceType: pgp.facilityServiceType,
            network: pgp.network,
            parentCompanyNote: pgp.parentCompanyNote,
            participatingNetworks: pgp.participatingNetworks,
            participationStatus: pgp.participationStatus,
            providerPgpId: pgp.providerPgpId,
            reimbursementType: pgp.reimbursementType,
          } as PACIMPgpWithProvider,
          isNpiFacility: false,
        },
        facilityContact,
        userWithoutFacilities: !facility.providerPgpDTOList.length
      };
    } else {
      return {
        provider: null,
        facilityContact,
        userWithoutFacilities: true,
      };
    }
  }

  private serializeOrderingProvider(data: PhysicianAccountPgpDTO): OrderingProviderExistingData {
    const physicianPgp = data?.physicianPgpDTO;

    return {
      provider: data ? {
        physicianAccountViewDTO: data.physicianAccountDTO,
        orderingPhysicianAccountId: physicianPgp?.physicianAccountId,
        orderingPhysicianPgpId: physicianPgp?.physicianPgpId,
        isGoldCardProvider: physicianPgp?.isGoldCardProvider,
        goldCardAuthorizationType: physicianPgp?.goldCardAuthorizationType
      } : null
    };
  }
}
